import { forwardRef } from "react";
import {
  Hand,
  HelpCircle,
  ShoppingBag,
  UserRound,
  CircleDashed,
} from "lucide-react";
import { Badge, type BadgeProps } from "./Badge";
import { cn } from "@/lib/cn";

/**
 * ConversationFsmBadge — chatbot session FSM holati.
 * Manba: docs/architecture/chatbot-data-model.md (ChatbotSession.fsmState)
 *
 * Holatlar:
 *   GREETING      — birinchi salomlashish, kontekst yig'ish
 *   FAQ           — umumiy savol-javob (RAG)
 *   BUYER         — sotib olish niyati (BUYER_INTENT pipeline)
 *   HUMAN_HANDOFF — operator ulangan
 */

export type ConversationFsmState =
  | "GREETING"
  | "FAQ"
  | "BUYER"
  | "HUMAN_HANDOFF";

interface Meta {
  label: string;
  icon: typeof Hand;
  variant: BadgeProps["variant"];
  cls?: string;
}

const META: Record<ConversationFsmState, Meta> = {
  GREETING: {
    label: "Salomlashish",
    icon: Hand,
    variant: "info",
  },
  FAQ: {
    label: "Savol-javob",
    icon: HelpCircle,
    variant: "default",
    cls: "bg-[var(--info-soft)] text-[var(--info-fg)] border-[var(--info-border)]",
  },
  BUYER: {
    label: "Xaridor",
    icon: ShoppingBag,
    variant: "default",
    cls: "bg-[var(--warning-soft)] text-[var(--warning-fg)] border-[var(--warning-border)]",
  },
  HUMAN_HANDOFF: {
    label: "Operator",
    icon: UserRound,
    variant: "warning",
  },
};

const FALLBACK: Meta = {
  label: "Aniqlanmagan",
  icon: CircleDashed,
  variant: "muted",
};

export interface ConversationFsmBadgeProps
  extends Omit<BadgeProps, "variant" | "children"> {
  state: ConversationFsmState | string | null | undefined;
  showIcon?: boolean;
}

export const ConversationFsmBadge = forwardRef<
  HTMLSpanElement,
  ConversationFsmBadgeProps
>(({ state, showIcon = true, className, size = "md", ...props }, ref) => {
  const key = (state ?? "").toString().toUpperCase() as ConversationFsmState;
  const meta = META[key] ?? FALLBACK;
  const Icon = meta.icon;
  return (
    <Badge
      ref={ref}
      variant={meta.variant}
      size={size}
      className={cn(meta.cls, className)}
      {...props}
    >
      {showIcon && <Icon className="h-3 w-3 shrink-0" aria-hidden />}
      {meta.label}
    </Badge>
  );
});
ConversationFsmBadge.displayName = "ConversationFsmBadge";
